import { WEEKDAYS } from './types.js';
// Datumrekenen zonder DST-valkuilen: intern een dagnummer (dagen sinds 1970-01-01, via
// Date.UTC). UTC kent geen zomertijd, dus +1 dag is altijd precies één kalenderdag.
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MS_PER_DAY = 86_400_000;
export function parseLocalDate(value) {
    const m = DATE_RE.exec(value);
    if (!m)
        throw new RangeError(`ongeldige datum: ${value}`);
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    const check = new Date(Date.UTC(year, month - 1, day));
    if (check.getUTCFullYear() !== year ||
        check.getUTCMonth() !== month - 1 ||
        check.getUTCDate() !== day) {
        throw new RangeError(`bestaat niet op de kalender: ${value}`);
    }
    return { year, month, day };
}
function pad2(n) {
    return n < 10 ? `0${n}` : String(n);
}
export function formatLocalDate(ymd) {
    return `${ymd.year}-${pad2(ymd.month)}-${pad2(ymd.day)}`;
}
/** Dagnummer sinds 1970-01-01 (kan negatief zijn). */
export function toDayNumber(value) {
    const { year, month, day } = parseLocalDate(value);
    return Math.round(Date.UTC(year, month - 1, day) / MS_PER_DAY);
}
export function fromDayNumber(dayNumber) {
    const d = new Date(dayNumber * MS_PER_DAY);
    return formatLocalDate({
        year: d.getUTCFullYear(),
        month: d.getUTCMonth() + 1,
        day: d.getUTCDate(),
    });
}
export function addDays(value, days) {
    return fromDayNumber(toDayNumber(value) + days);
}
/** `b − a` in dagen. */
export function diffDays(a, b) {
    return toDayNumber(b) - toDayNumber(a);
}
export function compareLocalDate(a, b) {
    // ISO-datums sorteren lexicografisch correct; parse toch om rommel te weigeren.
    parseLocalDate(a);
    parseLocalDate(b);
    return a < b ? -1 : a > b ? 1 : 0;
}
/** Weekdag van een datum; 1970-01-01 was een donderdag. */
export function weekdayOf(value) {
    const idx = (((toDayNumber(value) + 3) % 7) + 7) % 7; // 0 = MO
    return WEEKDAYS[idx] ?? 'MO';
}
export function weekdayIndex(weekday) {
    return WEEKDAYS.indexOf(weekday);
}
/** Eerste dag van de week waarin `value` valt, gegeven de weekstart. */
export function startOfWeek(value, weekStart = 'MO') {
    const offset = (weekdayIndex(weekdayOf(value)) - weekdayIndex(weekStart) + 7) % 7;
    return addDays(value, -offset);
}
export function isValidLocalDate(value) {
    try {
        parseLocalDate(value);
        return true;
    }
    catch {
        return false;
    }
}
export function isLeapYear(year) {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}
/** Aantal dagen in een maand (month 1-12). */
export function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
/** Weekdag van onderdelen (zonder string-omweg). */
export function weekdayOfYmd(ymd) {
    const n = Math.round(Date.UTC(ymd.year, ymd.month - 1, ymd.day) / MS_PER_DAY);
    return WEEKDAYS[(((n + 3) % 7) + 7) % 7] ?? 'MO';
}
/**
 * ISO 8601-week: week 1 bevat de eerste donderdag van het jaar; het weekjaar kan afwijken van
 * het kalenderjaar (2027-01-01 valt in 2026-W53). 2026 heeft 53 weken.
 */
export function isoWeek(value) {
    const n = toDayNumber(value);
    const isoDow = weekdayIndex(weekdayOf(value)); // 0 = MO
    const thursday = fromDayNumber(n - isoDow + 3);
    const year = parseLocalDate(thursday).year;
    const jan1 = toDayNumber(`${year}-01-01`);
    return { year, week: Math.floor((toDayNumber(thursday) - jan1) / 7) + 1 };
}
/** Maanden sinds jaar 0 (voor INTERVAL bij MONTHLY). */
export function monthIndex(ymd) {
    return ymd.year * 12 + (ymd.month - 1);
}
/** Minimum en maximum van ISO-datums (lexicografisch). */
export function minDate(a, b) {
    return a <= b ? a : b;
}
export function maxDate(a, b) {
    return a >= b ? a : b;
}
//# sourceMappingURL=localDate.js.map